/*
  Fluffberry social proof toast.

  Rotates through purchase entries rendered into the page by
  sections/fluffberry-social-proof.liquid. Entry values are customer-supplied,
  so messages are composed from DOM nodes and never via innerHTML.

  The toast is a polite live region, not a dialog: it never traps or steals
  focus, and it yields to any overlay that owns the screen.
*/

const FB_SP_SHOWN = 'fb_sp_shown'; /* sessionStorage: messages shown this visit */
const FB_SP_DISMISSED = 'fb_sp_dismissed'; /* storage: timestamp of last dismissal */
const FB_SP_FADE_MS = 520; /* matches the .52s CSS transition */

class FluffberrySocialProof extends HTMLElement {
  connectedCallback() {
    // A fixed-position overlay must only ever mount once.
    if (window.__fbSocialProof && window.__fbSocialProof !== this) return;
    window.__fbSocialProof = this;

    this.entries = Array.from(this.querySelectorAll('.fb-sp__entry'));
    if (!this.entries.length) return;

    this.toast = this.querySelector('[data-fb-sp-toast]');
    this.message = this.querySelector('[data-fb-sp-message]');
    this.media = this.querySelector('[data-fb-sp-media]');
    if (!this.toast || !this.message) return;

    this.template = this.dataset.template || '[name] from [city] bought [product]';
    this.displayMs = (parseInt(this.dataset.displayDuration, 10) || 6) * 1000;
    this.gapMs = (parseInt(this.dataset.gap, 10) || 14) * 1000;
    this.initialMs = (parseInt(this.dataset.initialDelay, 10) || 12) * 1000;
    this.dismissDays = parseInt(this.dataset.dismissDays, 10) || 0;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Repeated unrequested motion is what reduced-motion is asking to avoid, and
    // a single entry looping would just repeat itself.
    const cap = parseInt(this.dataset.maxPerSession, 10);
    this.cap = this.reduced || this.entries.length === 1 ? 1 : Number.isNaN(cap) || cap === 0 ? Infinity : cap;

    this.order = this.shuffle(this.entries.slice());
    this.cursor = 0;
    this.lastEntry = null;
    this.visible = false;

    if (this.dataset.designMode === 'true') {
      this.bindDesignMode();
      return;
    }

    if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return;
    if (this.isDismissed()) return;
    if (this.sessionCount() >= this.cap) return;

    this.bind();
    this.schedule(this.initialMs);
  }

  disconnectedCallback() {
    this.clearTimers();
    if (this.bodyObserver) this.bodyObserver.disconnect();
    if (this.atcObserver) this.atcObserver.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (window.__fbSocialProof === this) window.__fbSocialProof = null;
  }

  /* ---- setup ---- */

  bind() {
    const close = this.querySelector('[data-fb-sp-close]');
    if (close) close.addEventListener('click', this.dismiss.bind(this));

    if (this.dataset.pauseOnHover === 'true') {
      this.toast.addEventListener('mouseenter', this.pause.bind(this));
      this.toast.addEventListener('focusin', this.pause.bind(this));
      this.toast.addEventListener('mouseleave', this.resume.bind(this));
      this.toast.addEventListener('focusout', this.resume.bind(this));
    }

    // A background tab must not burn through the session cap.
    this.onVisibility = () => {
      if (document.hidden) this.pause();
      else if (this.visible) this.resume();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onResize = this.measureBlocker.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });

    // Hide the moment a drawer or modal takes over the screen.
    this.bodyObserver = new MutationObserver(() => {
      if (this.visible && this.isBlocked()) this.hide();
    });
    this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // The sticky add-to-cart bar toggles `hidden` on scroll; re-measure when it does.
    const atc = document.querySelector('fluffberry-sticky-atc');
    if (atc) {
      this.atcObserver = new MutationObserver(this.measureBlocker.bind(this));
      this.atcObserver.observe(atc, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  bindDesignMode() {
    const id = this.dataset.sectionId;
    document.addEventListener('shopify:section:select', (e) => {
      if (e.detail.sectionId === id) this.previewLoop();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail.sectionId === id) {
        this.clearTimers();
        this.hide();
      }
    });
  }

  /*
    Theme-editor preview. Cycles through the entries on a short, fixed rhythm so
    the merchant can actually see the animation and the variety while the
    section is selected, instead of waiting out shopper-facing delays that can
    run to half a minute between messages.

    Session caps, dismissal and the overlay guard are all ignored here: this is
    a preview of what a shopper eventually sees, not a simulation of pacing.
  */
  previewLoop() {
    this.clearTimers();
    const entry = this.nextEntry();
    if (!entry) return;

    this.render(entry);
    this.show();

    const onScreen = Math.min(Math.max(this.displayMs, 1800), 4000);
    this.hideTimer = setTimeout(() => {
      this.hide();
      // Wait out the fade so each message animates in cleanly.
      this.showTimer = setTimeout(this.previewLoop.bind(this), FB_SP_FADE_MS + 180);
    }, onScreen);
  }

  /* ---- scheduling ---- */

  schedule(delay) {
    this.clearTimers();
    this.showTimer = setTimeout(this.next.bind(this), delay);
  }

  next() {
    if (this.sessionCount() >= this.cap) return;

    // Something modal owns the screen: wait rather than stack on top of it.
    if (this.isBlocked()) {
      this.schedule(this.gapMs);
      return;
    }

    const entry = this.nextEntry();
    if (!entry) return;

    this.render(entry);
    this.show();
    this.bumpSessionCount();
    this.hideTimer = setTimeout(this.cycle.bind(this), this.displayMs);
  }

  cycle() {
    this.hide();
    if (this.sessionCount() >= this.cap) return;
    this.schedule(this.gapMs);
  }

  pause() {
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

  resume() {
    if (!this.visible || this.hideTimer) return;
    this.hideTimer = setTimeout(this.cycle.bind(this), this.displayMs);
  }

  clearTimers() {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    clearTimeout(this.fadeTimer);
  }

  /* ---- entry selection ---- */

  shuffle(list) {
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }

  nextEntry() {
    if (this.cursor >= this.order.length) {
      // Reshuffle so a second pass differs from the first.
      this.order = this.shuffle(this.order);
      this.cursor = 0;
    }
    let entry = this.order[this.cursor];
    this.cursor += 1;

    // Never the same message twice running.
    if (entry === this.lastEntry && this.order.length > 1) {
      entry = this.order[this.cursor] || this.order[0];
      this.cursor += 1;
    }
    this.lastEntry = entry;
    return entry;
  }

  /* ---- rendering ---- */

  render(entry) {
    if (!entry) return;
    this.message.textContent = '';

    if (entry.dataset.message) {
      // Fallback brand note: shown verbatim, never run through the tokens.
      this.message.appendChild(document.createTextNode(entry.dataset.message));
    } else {
      const parts = this.template.split(/(\[name\]|\[city\]|\[product\])/g);
      parts.forEach((part) => {
        if (!part) return;
        if (part === '[name]') this.message.appendChild(this.strong(entry.dataset.name));
        else if (part === '[city]') this.message.appendChild(document.createTextNode(entry.dataset.city || ''));
        else if (part === '[product]') this.message.appendChild(this.productNode(entry));
        else this.message.appendChild(document.createTextNode(part));
      });
    }

    this.paintMedia(entry);
  }

  strong(text) {
    const el = document.createElement('strong');
    el.textContent = text || '';
    return el;
  }

  productNode(entry) {
    const title = entry.dataset.product || '';
    if (!entry.dataset.url) return this.strong(title);
    const a = document.createElement('a');
    a.href = entry.dataset.url;
    a.textContent = title;
    return a;
  }

  paintMedia(entry) {
    if (!this.media) return;
    const src = entry.dataset.image;
    if (!src) {
      this.media.hidden = true;
      return;
    }
    // Created once, then only re-pointed, so exactly one thumbnail is ever in flight.
    if (!this.mediaImg) {
      this.mediaImg = document.createElement('img');
      this.mediaImg.width = 56;
      this.mediaImg.height = 56;
      this.media.appendChild(this.mediaImg);
    }
    this.mediaImg.src = src;
    this.mediaImg.alt = entry.dataset.alt || '';
    this.media.hidden = false;
  }

  /* ---- visibility ---- */

  show() {
    this.measureBlocker();
    this.hidden = false;
    this.visible = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('is-visible'));
    });
  }

  hide() {
    this.classList.remove('is-visible');
    this.visible = false;
    clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      this.hidden = true;
    }, FB_SP_FADE_MS);
  }

  dismiss() {
    this.clearTimers();
    this.hide();
    const now = String(Date.now());
    try {
      if (this.dismissDays > 0) localStorage.setItem(FB_SP_DISMISSED, now);
      else sessionStorage.setItem(FB_SP_DISMISSED, now);
    } catch (e) {
      /* private mode: dismissal lasts for this page only */
    }
  }

  isDismissed() {
    try {
      if (this.dismissDays > 0) {
        const at = parseInt(localStorage.getItem(FB_SP_DISMISSED), 10) || 0;
        return at > 0 && Date.now() - at < this.dismissDays * 86400000;
      }
      return !!sessionStorage.getItem(FB_SP_DISMISSED);
    } catch (e) {
      return false;
    }
  }

  /* ---- guards ---- */

  isBlocked() {
    if (document.hidden) return true;
    // Dawn sets this on the body for drawers and modals.
    if (document.body.classList.contains('overflow-hidden')) return true;
    // The sign-up popup never sets it, so it needs its own check.
    if (document.querySelector('.fb-pop.is-open')) return true;
    if (document.querySelector('cart-drawer.active')) return true;
    return false;
  }

  measureBlocker() {
    const atc = document.querySelector('fluffberry-sticky-atc:not([hidden])');
    const height = atc ? Math.ceil(atc.getBoundingClientRect().height) : 0;
    this.style.setProperty('--fb-sp-blocker', `${height}px`);
  }

  /* ---- session counting ---- */

  sessionCount() {
    try {
      return parseInt(sessionStorage.getItem(FB_SP_SHOWN), 10) || 0;
    } catch (e) {
      return this.localCount || 0;
    }
  }

  bumpSessionCount() {
    const next = this.sessionCount() + 1;
    this.localCount = next;
    try {
      sessionStorage.setItem(FB_SP_SHOWN, String(next));
    } catch (e) {
      /* private mode: the in-memory count still bounds this page */
    }
  }
}

if (!customElements.get('fluffberry-social-proof')) {
  customElements.define('fluffberry-social-proof', FluffberrySocialProof);
}
